import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Upload, Plus, Trash2, Users, FileUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const StaticListDetailPage = () => {
  const { listId } = useParams();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [addEmails, setAddEmails] = useState('');
  const [addPhones, setAddPhones] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/segments/static-lists/${listId}`, { headers });
      setList(r.data);
    } catch (e) { toast.error('Load failed'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [listId]);

  const addMembers = async () => {
    const emails = addEmails.split(/[\n,;]/).map(s => s.trim()).filter(Boolean);
    const phones = addPhones.split(/[\n,;]/).map(s => s.trim()).filter(Boolean);
    if (!emails.length && !phones.length) { toast.error('Enter at least one email or phone'); return; }
    try {
      await axios.post(`${API}/segments/static-lists/${listId}/members`, { emails, phones }, { headers });
      toast.success('Members added');
      setAddOpen(false);
      setAddEmails('');
      setAddPhones('');
      load();
    } catch (e) { toast.error('Add failed'); }
  };

  const uploadCSV = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/segments/static-lists/${listId}/import-csv`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Imported ${r.data.imported.emails} emails + ${r.data.imported.phones} phones`);
      load();
    } catch (e) {
      toast.error('Upload failed: ' + (e?.response?.data?.detail || e.message));
    } finally { setUploading(false); }
  };

  const removeAll = async () => {
    if (!window.confirm('Remove ALL members from this list?')) return;
    try {
      await axios.delete(`${API}/segments/static-lists/${listId}/members`, {
        headers, data: {
          emails: list?.member_emails || [],
          phones: list?.member_phones || [],
          customer_ids: list?.member_customer_ids || [],
        }
      });
      toast.success('Cleared');
      load();
    } catch (e) { toast.error('Clear failed'); }
  };

  if (loading || !list) return <div className="p-8 text-gray-400">Loading…</div>;

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="static-list-detail">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/customers/segments')}>
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-xl md:text-2xl font-semibold text-white">{list.name}</h1>
            <p className="text-sm text-gray-400">{list.description || 'Static list — members are frozen until manually updated.'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setAddOpen(true)} data-testid="add-members-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Members
          </Button>
          <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={(e) => uploadCSV(e.target.files?.[0])} />
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-csv-btn">
            <FileUp className="w-4 h-4 mr-1" /> {uploading ? 'Uploading…' : 'Upload CSV'}
          </Button>
          {((list.member_emails?.length || 0) + (list.member_phones?.length || 0) > 0) && (
            <Button variant="ghost" size="sm" onClick={removeAll}>
              <Trash2 className="w-4 h-4 mr-1 text-red-400" /> Clear
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label="Total Members" value={list.member_count || 0} />
        <StatCard label="Emails" value={(list.member_emails || []).length} />
        <StatCard label="Phones" value={(list.member_phones || []).length} />
      </div>

      <div className="bg-wt-surface border border-wt-border rounded-lg p-4">
        <div className="text-sm font-semibold text-white mb-2">CSV format</div>
        <div className="text-xs text-gray-400 mb-3">
          Headers (case-insensitive): <code className="text-purple-300">email</code>, <code className="text-purple-300">phone</code>, <code className="text-purple-300">customer_id</code>. Any mix supported.
        </div>
        <pre className="text-xs bg-wt-bg p-2 rounded overflow-x-auto">email,phone{'\n'}jane@example.com,+971501234567{'\n'}bob@example.com,</pre>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <MemberPanel title="Emails" items={list.member_emails || []} />
        <MemberPanel title="Phones" items={list.member_phones || []} />
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-wt-surface border-wt-border">
          <DialogHeader><DialogTitle className="text-white">Add Members</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-white">Emails (one per line or comma separated)</Label>
              <Textarea value={addEmails} onChange={(e) => setAddEmails(e.target.value)} rows={5} className="bg-wt-bg mt-1" placeholder="jane@example.com&#10;bob@example.com" data-testid="add-emails-input" />
            </div>
            <div>
              <Label className="text-white">Phones (one per line or comma separated)</Label>
              <Textarea value={addPhones} onChange={(e) => setAddPhones(e.target.value)} rows={4} className="bg-wt-bg mt-1" placeholder="+971501234567&#10;+919876543210" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={addMembers} data-testid="submit-add-members">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StatCard = ({ label, value }) => (
  <div className="p-4 bg-wt-surface border border-wt-border rounded-lg">
    <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-gray-500"><Users className="w-3 h-3" /> {label}</div>
    <div className="text-3xl font-bold text-white mt-1">{(value || 0).toLocaleString()}</div>
  </div>
);

const MemberPanel = ({ title, items }) => (
  <div className="bg-wt-surface border border-wt-border rounded-lg p-4">
    <div className="text-sm font-semibold text-white mb-2">{title} · {items.length}</div>
    <div className="max-h-80 overflow-y-auto space-y-1">
      {items.length === 0 && <div className="text-xs text-gray-500 py-4 text-center">No entries</div>}
      {items.slice(0, 500).map((m, i) => (
        <div key={i} className="text-xs text-gray-300 px-2 py-1 bg-wt-bg rounded border border-wt-border truncate">{m}</div>
      ))}
      {items.length > 500 && <div className="text-xs text-gray-500 italic pt-2">(showing first 500 of {items.length})</div>}
    </div>
  </div>
);

export default StaticListDetailPage;
